import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import LeadCard from '../LeadCard';
import classNames from 'classnames';
import LeadsContext from '../../providers/Leads/Leads.context';
import { Lead, LeadState, WaitlistedStages } from 'models/Lead';
import {
  getGridLeadsEmptyState,
  isLeadStage,
  isWaitlisted,
  WAITLIST_COLUMN_POPUP,
  WAITLIST_COLUMN_TITLES,
} from '../../utils';
import { useTranslation } from 'react-i18next';
import { LEAD_DRAWER_OPTIONS } from '../../../../models';
import ListingContext from '../../providers/Listing/Listing.context';
import { TRANSITION_TIME } from 'data/global';
import ResolvePopup from 'components/ResolvePopup';
import InformationcircleIcon from 'icons/Informationcircle.icon';

import './LeadsDragContainers.styles.scss';

type LeadsDragContainersProps = {
  className?: string;
};

type TargetRefObject = {
  target: HTMLElement;
  lead: Lead;
  containerWidth: number;
};

type OverlappedContainer = {
  key: LeadState;
  boundingClient: Record<string, any>;
};

type CardRef = {
  ref: HTMLDivElement;
  leadId: string;
};

const MAX_CONTAINER_HEIGHT = 780; // px
const MIN_CONTAINER_HEIGHT = 180; // px
const CARD_HEIGHT = 286; // px
const CONTAINER_CARD_GAP = 24;
const CARD_OFFSET = 26; //px
const HOVERED_CARD_CLASS = 'hovered-card';
const RESET_CARD_STYLE =
  'position: relative; top: 0; left: 0; transform: unset; transition: unset;';
const GRABBING_CARD_STYLE = `position:fixed; z-index:20; transition: unset; transform:translate3d(calc(-100% + ${CARD_OFFSET}px),calc(-100% + ${CARD_OFFSET}px),0);`;

const LeadsDragContainers: React.FC<LeadsDragContainersProps> = (props) => {
  const { className } = props;

  const {
    ongoingLeads,
    gridLeads,
    waitlistedLeads,
    scrollableContainer,
    numberOfWaitlistedGridLeads,
    changeState,
    checkIsLeadUpdating,
    openLeadDrawer,
    openWithdrawModal,
    updateLead,
  } = useContext(LeadsContext);
  const { workflowConfigurationStepsCapacity, canManageDeal } =
    useContext(ListingContext);

  const containersRefs = useRef([]);
  const cardsRefs = useRef<Record<LeadState, CardRef[]>>(
    getGridLeadsEmptyState(),
  );
  const targetedCard = useRef<TargetRefObject>(null);
  const overlappedContainer = useRef<OverlappedContainer>(null);
  const overlappedIndex = useRef<number>(0);
  const oldOverlappedIndex = useRef<number>(0);
  const oldOverlappedContainer = useRef<OverlappedContainer>(null);
  const [boundingClients, setBoundingClients] = useState<OverlappedContainer[]>(
    [],
  );

  const classes = classNames('leads-drag-containers', className);

  const { t } = useTranslation();

  const [largestOngoingArray, largestWaitlistedArray] = useMemo(() => {
    const getMaxArray = (leads: Record<LeadState, Lead[]>) =>
      Object.values(leads).reduce(
        (acc, arr) => (acc > arr.length ? acc : arr.length),
        0,
      );

    return [getMaxArray(ongoingLeads), getMaxArray(waitlistedLeads)];
  }, [ongoingLeads, waitlistedLeads]);

  const checkCardOverlapping = useCallback(
    (containerKey: LeadState) => {
      const { left, right, top, bottom } =
        targetedCard.current.target.getBoundingClientRect();

      const horisontalOffset = (right - left) / 2;
      const verticalOffset = (bottom - top) / 2;

      const index = cardsRefs.current[containerKey].findIndex((ref) => {
        if (!ref || !ref.ref || targetedCard.current?.lead._id === ref.leadId)
          return false;

        const {
          left: cardLeft,
          right: cardRight,
          top: cardTop,
          bottom: cardBottom,
        } = ref.ref.getBoundingClientRect();

        const cardHeight = cardBottom - cardTop;

        return (
          cardLeft <= right - horisontalOffset &&
          cardRight >= left + horisontalOffset &&
          cardBottom - cardHeight / 2 >= top + verticalOffset &&
          cardTop - (3 * cardHeight) / 2 - CONTAINER_CARD_GAP <
            bottom - verticalOffset
        );
      });
      return index !== -1 ? index : gridLeads[containerKey]?.length;
    },
    [gridLeads],
  );

  const checkContainerOverlapping = useCallback(() => {
    const { right, bottom } =
      targetedCard.current.target.getBoundingClientRect();

    const bottomOffset = bottom - CARD_OFFSET;
    const rightOffset = right - CARD_OFFSET;

    return boundingClients.find(({ boundingClient }) => {
      const {
        left: containerLeft,
        right: containerRight,
        top: containerTop,
        bottom: containerBottom,
      } = boundingClient;

      return (
        containerLeft < rightOffset &&
        containerRight > rightOffset &&
        containerBottom > bottomOffset &&
        containerTop < bottomOffset
      );
    });
  }, [boundingClients]);

  const onMouseMoveCallback = useCallback(
    (mouseEvent: globalThis.MouseEvent) => {
      const { clientX, clientY } = mouseEvent;

      if (!targetedCard.current) return;

      const { target, containerWidth } = targetedCard.current;

      target.setAttribute(
        'style',
        `${GRABBING_CARD_STYLE} top:${clientY}px; left:${clientX}px; width:${containerWidth}px;`,
      );
      overlappedContainer.current = checkContainerOverlapping();
      target.classList.remove('disabled-for-dropping');

      if (!overlappedContainer.current) {
        target.classList.add('disabled-for-dropping');
        return;
      }

      overlappedIndex.current = overlappedContainer.current?.key
        ? checkCardOverlapping(overlappedContainer.current.key)
        : 0;

      cardsRefs.current[overlappedContainer.current.key][
        overlappedIndex.current
      ]?.ref?.classList.add(HOVERED_CARD_CLASS);

      if (
        oldOverlappedContainer.current?.key &&
        oldOverlappedContainer.current?.key !== overlappedContainer.current?.key
      ) {
        cardsRefs.current[oldOverlappedContainer.current.key][
          oldOverlappedIndex.current
        ]?.ref?.classList.remove(HOVERED_CARD_CLASS);
      }

      if (oldOverlappedIndex.current !== overlappedIndex.current) {
        cardsRefs.current[overlappedContainer.current.key][
          oldOverlappedIndex.current
        ]?.ref?.classList.remove(HOVERED_CARD_CLASS);
      }

      oldOverlappedIndex.current = overlappedIndex.current;
      oldOverlappedContainer.current = overlappedContainer.current;
    },
    [checkCardOverlapping, checkContainerOverlapping],
  );

  const onCardMouseDown = useCallback(
    (lead: Lead, containerKey: LeadState, index: number) => {
      const card = cardsRefs.current[containerKey][index];
      const container = containersRefs.current.find(
        ({ key }) => key === containerKey,
      )?.ref;

      if (!container) return;

      const containerStyles = window.getComputedStyle(container);

      const paddingLeft = parseFloat(containerStyles.paddingLeft) || 0;
      const paddingRight = parseFloat(containerStyles.paddingRight) || 0;

      const containerWidth = container.clientWidth - paddingLeft - paddingRight;

      targetedCard.current = {
        target: card.ref,
        lead,
        containerWidth,
      };

      window.addEventListener('mousemove', onMouseMoveCallback);
    },
    [onMouseMoveCallback],
  );

  const resetRefs = useCallback(() => {
    if (
      overlappedContainer.current?.key &&
      cardsRefs.current[overlappedContainer.current.key][
        overlappedIndex.current
      ]?.ref
    )
      cardsRefs.current[overlappedContainer.current.key][
        overlappedIndex.current
      ]?.ref?.classList.remove(HOVERED_CARD_CLASS);
    else
      document
        .querySelector(`.${HOVERED_CARD_CLASS}`)
        ?.classList.remove(HOVERED_CARD_CLASS);

    targetedCard.current.target.classList.remove('disabled-for-dropping');
    targetedCard.current.target.setAttribute('style', RESET_CARD_STYLE);

    const hoveredCard = targetedCard.current.target;
    targetedCard.current = null;
    overlappedContainer.current = null;
    window.removeEventListener('mousemove', onMouseMoveCallback);

    setTimeout(() => {
      hoveredCard.removeAttribute('style');
    }, TRANSITION_TIME);
  }, [onMouseMoveCallback]);

  const onMouseUp = useCallback(() => {
    containersRefs.current.forEach(({ ref }) => {
      ref.classList?.remove('invalid');
    });

    if (overlappedContainer.current?.key && targetedCard.current)
      isWaitlisted(overlappedContainer.current.key) &&
      isLeadStage(targetedCard.current.lead.leadState)
        ? openWithdrawModal(targetedCard.current.lead)
        : changeState(
            targetedCard.current.lead,
            overlappedContainer.current.key,
            overlappedIndex.current,
          );

    resetRefs();
  }, [changeState, openWithdrawModal, resetRefs]);

  const renderCards = useCallback(
    (leadsArray: Lead[], containerKey: LeadState) =>
      leadsArray.map((lead, index) => (
        <LeadCard
          key={lead._id}
          lead={lead}
          className={classNames({ disabled: checkIsLeadUpdating(lead._id) })}
          isDisabled={!canManageDeal}
          ref={(ref) =>
            (cardsRefs.current[containerKey][index] = {
              ref,
              leadId: lead._id,
            })
          }
          updateLead={updateLead}
          onMouseUp={onMouseUp}
          onMouseDown={() => onCardMouseDown(lead, containerKey, index)}
          onLeadSelect={() => openLeadDrawer(lead)}
          onMessage={() => openLeadDrawer(lead, LEAD_DRAWER_OPTIONS.MESSAGES)}
          onNotesSelect={() => openLeadDrawer(lead, LEAD_DRAWER_OPTIONS.NOTES)}
        />
      )),
    [
      canManageDeal,
      onCardMouseDown,
      onMouseUp,
      checkIsLeadUpdating,
      openLeadDrawer,
      updateLead,
    ],
  );

  const renderContainers = useMemo(() => {
    const ongoingLeadsArray = Object.entries(ongoingLeads);
    const waitlistedLeadsArray = Object.entries(waitlistedLeads);

    return (
      <>
        <div className="leads-drag-containers__container-row">
          {ongoingLeadsArray.map(([key, value], index) => (
            <div
              key={key}
              ref={(ref) => (containersRefs.current[index] = { key, ref })}
              style={{
                maxHeight: `${MAX_CONTAINER_HEIGHT}px`,
                minHeight: largestOngoingArray
                  ? largestOngoingArray * CARD_HEIGHT > MAX_CONTAINER_HEIGHT
                    ? MAX_CONTAINER_HEIGHT
                    : largestOngoingArray * CARD_HEIGHT
                  : `${MIN_CONTAINER_HEIGHT}px`,
                overflow: 'auto',
              }}
              className={classNames({
                full:
                  workflowConfigurationStepsCapacity[key as LeadState] <=
                  value.length,
              })}
            >
              {renderCards(value, key as LeadState)}
            </div>
          ))}
        </div>

        <div className="leads-drag-containers__container-row">
          {waitlistedLeadsArray.map(([key, value], index) => (
            <div
              key={key}
              ref={(ref) =>
                (containersRefs.current[index + ongoingLeadsArray.length] = {
                  key,
                  ref,
                })
              }
              style={{
                maxHeight: `${MAX_CONTAINER_HEIGHT}px`,
                minHeight: largestWaitlistedArray
                  ? largestWaitlistedArray * CARD_HEIGHT > MAX_CONTAINER_HEIGHT
                    ? MAX_CONTAINER_HEIGHT
                    : largestWaitlistedArray * CARD_HEIGHT
                  : `${MIN_CONTAINER_HEIGHT}px`,
                overflow: 'auto',
              }}
            >
              <p className="waitlist-title">
                {t(WAITLIST_COLUMN_TITLES[key as WaitlistedStages])}
                <span>
                  {value.length}/{numberOfWaitlistedGridLeads}
                </span>
                <ResolvePopup
                  onHover
                  popupContent={
                    <p>{t(WAITLIST_COLUMN_POPUP[key as WaitlistedStages])}</p>
                  }
                >
                  <InformationcircleIcon />
                </ResolvePopup>
              </p>
              {renderCards(value, key as LeadState)}
            </div>
          ))}
        </div>
      </>
    );
  }, [
    numberOfWaitlistedGridLeads,
    largestOngoingArray,
    largestWaitlistedArray,
    ongoingLeads,
    waitlistedLeads,
    workflowConfigurationStepsCapacity,
    renderCards,
    t,
  ]);

  useEffect(() => {
    const updateBoundingClients = () => {
      setBoundingClients(
        containersRefs.current.map(({ key, ref }) => ({
          key,
          boundingClient: ref?.getBoundingClientRect(),
        })),
      );
    };
    updateBoundingClients();

    scrollableContainer?.addEventListener('scroll', updateBoundingClients);
    window.addEventListener('resize', updateBoundingClients);
    window.addEventListener('scroll', updateBoundingClients);

    return () => {
      scrollableContainer?.removeEventListener('scroll', updateBoundingClients);
      window.removeEventListener('resize', updateBoundingClients);
      window.removeEventListener('scroll', updateBoundingClients);
    };
  }, [scrollableContainer, largestOngoingArray, largestWaitlistedArray]); // update clients on container height change(which depends on longest array)

  return <div className={classes}>{renderContainers}</div>;
};

export default LeadsDragContainers;
